import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import type { AiDocumentReviewConfig } from 'src/interfaces/aiDocumentReviewConfig'
import { getAiDocumentReviewConfigsSaving } from 'src/selectors/aiDocumentReviewConfig'
import { formatStepLabel } from 'src/utils/aiReviewConfigFormatters'

interface Props {
  /** `null` keeps the modal closed; a config both opens it and names the row. */
  config: AiDocumentReviewConfig | null
  onCancel(): void
  onConfirm(): void
}

const DeleteAiReviewConfigModalComponent = ({ config, onCancel, onConfirm }: Props) => {
  const isSaving = useSelector(getAiDocumentReviewConfigsSaving)

  if (!config) {
    return null
  }

  return (
    <CommonModal
      isOpen
      title="Delete this configuration?"
      onClose={onCancel}
      buttons={
        <Fragment>
          <Button size="small" color="grey" disabled={isSaving} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="small"
            color="red"
            loading={isSaving}
            disabled={isSaving}
            onClick={onConfirm}
            data-testid="ai-review-config-delete-confirm-button"
          >
            Delete
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-2`}>
        <Typography variant="body2">
          {`${stepLabel(config.stepType)} — ${formatStepLabel(config)}`}
        </Typography>
        <Typography variant="body2" css={tw`text-shades-500`}>
          The document goes back to the partner&apos;s default: reviewed in shadow mode with the
          standard instructions. Nothing already reviewed changes.
        </Typography>
      </div>
    </CommonModal>
  )
}

export const DeleteAiReviewConfigModal = memo(DeleteAiReviewConfigModalComponent)
